import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api'
import { ApiResponse, ApiError } from '../types/api'
import * as geojson from 'geojson';

export const useFireHistory = (projectId: string) => {
    if (!projectId) {
        return { project: null, isLoading: false, error: null };
    }
    const { data: history, isLoading, error } = useQuery({
        queryKey: [projectId, 'fireHistory'],
        queryFn:  () => api.request<ApiResponse<any>>(`/api/projects/${projectId}/fireHistory`),
        refetchOnWindowFocus: false
    }) as { data: ApiResponse<any>, isLoading: boolean, error: ApiError };

    return { history, isLoading, error };
};

export interface DateConfig {
    mode: 'fixed' | 'field';
    value?: string | null;  // for fixed mode: "YYYY-MM-DD"
    field?: string;         // for field mode: property field name
    format?: string;        // optional format hint like "DD/MM/YYYY"
}

export interface NameConfig {
    mode: 'fixed' | 'field';
    value?: string | null;  // for fixed mode
    field?: string;         // for field mode: property field name
}

export interface CreateFireHistoryPayload {
    geojson: geojson.FeatureCollection;
    dateConfig: DateConfig;
    nameConfig: NameConfig;
}

export interface CreateFireHistoryResult {
    created_count: number;
}

export const useCreateFireHistory = (projectId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CreateFireHistoryResult>, ApiError, CreateFireHistoryPayload>({
        mutationFn: async (payload: CreateFireHistoryPayload) => {
            return api.request<ApiResponse<CreateFireHistoryResult>>(
                `/api/projects/${projectId}/fireHistory`,
                {
                    method: 'POST',
                    body: {
                        geojson: payload.geojson,
                        date_config: payload.dateConfig,
                        name_config: payload.nameConfig,
                    },
                }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'fireHistory'] });
        },
    });
};

type UpdateFireHistoryPayload = {
    fireHistoryId: string | number;
    updates: {
        startDate?: string | null;
        endDate?: string | null;
    };
};

export const useUpdateFireHistory = (projectId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, UpdateFireHistoryPayload>({
        mutationFn: ({ fireHistoryId, updates }) =>
            api.request<ApiResponse<any>>(
                `/api/projects/${projectId}/fireHistory/${fireHistoryId}`,
                {
                    method: 'PATCH',
                    body: updates,
                }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'fireHistory'] });
        },
    });
};